import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, MarkerType, ConnectionMode } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TechniqueNode from "@/components/TechniqueNode";
import FlowEdge from "@/components/FlowEdge";
import TechniqueDetailDialog from "@/components/TechniqueDetailDialog";
import AddToLibraryDialog from "@/components/AddToLibraryDialog";
import { ScopeBadge } from "@/components/ScopeBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

const nodeTypes = { technique: TechniqueNode };
const edgeTypes = { flow: FlowEdge };
const ARROW = { type: MarkerType.ArrowClosed, color: "#E63946", width: 18, height: 18 };

function View() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seq, setSeq] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [preview, setPreview] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const openPreview = useCallback((t) => setPreview(t), []);

  useEffect(() => {
    api.get(`/sequences/${id}`).then(({ data }) => {
      setSeq(data);
      setNodes((data.nodes || []).map((n) => ({ ...n, type: "technique", data: { ...n.data, onOpen: openPreview } })));
      setEdges((data.edges || []).map((e) => ({
        id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle,
        type: "flow", animated: true,
        markerEnd: ARROW, markerStart: e.bidirectional ? ARROW : undefined,
        data: { bidirectional: !!e.bidirectional },
      })));
    }).catch((e) => { toast.error(apiError(e)); navigate("/sequences"); });
  }, [id, navigate, openPreview]);

  const isPA = user?.role === "platform_admin";
  const canEdit = seq && (seq.scope === "platform" ? isPA
    : isPA ? false
    : seq.scope === "personal" ? seq.owner_id === user?.user_id
    : (user?.role === "admin" || user?.role === "coach") && seq.gym_id === user?.gym_id);
  const canAddToLibrary = seq?.scope === "platform" && !isPA;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="h-14 flex items-center justify-between gap-2 px-3 sm:px-4 border-b border-border bg-black/60 backdrop-blur-xl shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Button data-testid="view-back-btn" variant="ghost" size="sm" onClick={() => navigate("/sequences")} className="text-muted-foreground hover:text-white shrink-0 px-2 h-10 min-w-10 sm:h-8 sm:min-w-0"><ArrowLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Back</span></Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-semibold truncate flex items-center gap-2 text-sm sm:text-base">{seq?.name}
              {seq && <ScopeBadge scope={seq.scope} className="border border-border text-muted-foreground shrink-0" />}
            </h1>
          </div>
        </div>
        {canAddToLibrary ? (
          <Button data-testid="view-add-to-library-btn" onClick={() => setAddOpen(true)} className="bg-primary hover:bg-primary/90 text-white shrink-0 px-2.5 sm:px-3">
            <BookmarkPlus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Add to Library</span>
          </Button>
        ) : canEdit && <Button data-testid="view-edit-btn" onClick={() => navigate(`/sequences/${id}/edit`)} variant="outline" className="border-border shrink-0 px-2.5 sm:px-3"><Pencil className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Edit</span></Button>}
      </div>

      <div className="flex-1 relative">
        <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} connectionMode={ConnectionMode.Loose} colorMode="dark" fitView nodesDraggable={false} nodesConnectable={false} proOptions={{ hideAttribution: true }}>
          <Background color="#27272A" gap={20} />
          <Controls showInteractive={false} />
          <MiniMap nodeColor="#E63946" maskColor="rgba(0,0,0,0.7)" />
        </ReactFlow>
        {nodes.length === 0 && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><p className="text-muted-foreground">This sequence is empty.</p></div>}
      </div>

      <TechniqueDetailDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} technique={preview} />
      <AddToLibraryDialog open={addOpen} onOpenChange={setAddOpen} item={seq} kind="sequence" />
    </div>
  );
}

export default function SequenceView() {
  return <ReactFlowProvider><View /></ReactFlowProvider>;
}
